import type { User, UserPublic } from '@/types';

/**
 * Checks whether the current user has platform-wide SuperAdmin privileges,
 * granting them the ability to manage SuperAdmin roles for other users.
 *
 * Only global super admins (user.is_super_admin === true) can grant or
 * revoke the SuperAdmin role for other users.
 *
 * @param currentUser - The currently authenticated user, or null if not authenticated
 * @returns true if the current user can manage SuperAdmin roles
 *
 * @example
 * ```typescript
 * if (canManageSuperAdminRole(currentUser)) {
 *   // Show SuperAdmin toggle UI
 * }
 * ```
 */
export const canManageSuperAdminRole = (currentUser: User | null | undefined): boolean => {
  return currentUser?.is_super_admin ?? false;
};

/**
 * Checks whether the given user has the SuperAdmin role in their current org context.
 *
 * @param user - A UserPublic object from the org users list
 * @returns true if the user has the SuperAdmin role
 *
 * @example
 * ```typescript
 * const isSA = isUserSuperAdminInOrg(user);
 * ```
 */
export const isUserSuperAdminInOrg = (user: UserPublic, orgId: string): boolean => {
  return user.allowed_roles?.[orgId]?.includes('super_user') ?? false;
};

/**
 * Checks whether the current user is attempting to modify their own SuperAdmin status.
 * Self-modification is not permitted to prevent accidental privilege loss.
 *
 * @param currentUserId - The ID of the currently authenticated user
 * @param targetUserId - The ID of the user whose SuperAdmin status would be modified
 * @returns true if the current user is the same as the target user
 *
 * @example
 * ```typescript
 * if (isSelfModification(currentUser.id, targetUser.id)) {
 *   // Disable the toggle
 * }
 * ```
 */
export const isSelfModification = (currentUserId: string, targetUserId: string): boolean => {
  return currentUserId === targetUserId;
};
